import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  defer,
  forkJoin,
  Observable,
  of,
  ReplaySubject,
  Subject,
  Subscription,
  throwError
} from 'rxjs';
import { OrderDTO, OrderItemWithValue, ShipmentLocationDTO } from './types';
import {
  JobsiteDTO,
  OrderStatusCode,
  DateRange,
  PathConstants
} from '../shared/types';
import { JobsiteOption, Load } from './types';
import * as moment_ from 'moment';
import {
  catchError,
  filter,
  map,
  reduce,
  switchMap,
  withLatestFrom
} from 'rxjs/operators';
import { UNIT_OF_MEASUREMENT_FOR_INDICATORS } from './constants';
import { CmxHttpClient } from '../../../../../../../services/http-client.service';
import { AppFeatures } from '../../../../../../../../toggleConfig';
import { FeatureToggleService } from '../../../../../../../../../src/app/feature-toggle-service/v1/src/feature-toggle.service';
import { StorageService } from '../../../../../../../services';
import { SessionService } from '../../../../../../../angular-services-v8/session.service';

const moment = moment_;

@Injectable()
export class CmxDashboardActiveLoadsCardService implements OnDestroy {
  private isFeatureToggleLoaded$ = this.featureToggleService.isLoaded$.pipe(
    filter(isLoaded => isLoaded)
  );

  public isNewDashboard2025Enabled$ = this.isFeatureToggleLoaded$.pipe(
    map(() =>
      this.featureToggleService.isEnabledFor(AppFeatures.NewDashboard2025, {
        country: this.storageService.userCountry
      })
    )
  );

  public legalEntity$ = this.storageService.legalEntityChanged$.pipe(
    map(
      () =>
        this.storageService.selectedLegalEntity ||
        this.sessionService.currentLegalEntityValue
    )
  );

  public country$ = this.legalEntity$.pipe(
    map(lE => lE.countryCode || this.sessionService.userCountry)
  );

  public unit$ = this.country$.pipe(
    switchMap(country => this.fetchParameters(country)),
    map(parameters => {
      if (parameters && parameters.length) {
        const parameter = parameters.find(
          ({ parameterDesc }) =>
            parameterDesc === 'UNIT_OF_MEASUREMENT_FOR_INDICATORS'
        );
        if (parameter) {
          return parameter.parameterValue;
        }
      }
      console.error(
        'Something is wrong with parameter fetching request. ',
        "Parameter 'UNIT_OF_MEASUREMENT_FOR_INDICATORS' couldn't be found, fall back to default value"
      );
      return null;
    })
  );

  /**
   * active loads collection (output)
   */
  public activeLoads$ = new BehaviorSubject<Load[]>([]);
  /**
   * complete loads collection (output)
   */
  public completeLoads$ = new BehaviorSubject<Load[]>([]);
  /**
   * planned loads collection (output)
   */
  public plannedLoads$ = new BehaviorSubject<Load[]>([]);
  /**
   * jobsites collection to pick from (output)
   */
  public jobsitesToChoose$ = new BehaviorSubject<JobsiteOption[]>([]);
  /**
   * loading status (output)
   */
  public status$ = new BehaviorSubject<LoadingStatus>(LoadingStatus.IDLE);

  /**
   * date range to fetch for. Next value initiates a new request (input)
   */
  public dateRange$ = new BehaviorSubject<DateRange>(null);

  constructor(
    private httpClient: CmxHttpClient,
    public featureToggleService: FeatureToggleService,
    public storageService: StorageService,
    private sessionService: SessionService
  ) {}

  /**
   * Sets status and erase data in case of error
   * @param newStatus new status
   */
  private setStatus(newStatus: LoadingStatus) {
    this.status$.next(newStatus);
    if (newStatus === LoadingStatus.ERROR) {
      setData([], this.activeLoads$);
      setData([], this.completeLoads$);
      setData([], this.plannedLoads$);
    }
  }
  /**
   * Sets initially data for loads table
   * @param activeLoads active loads array
   * @param completeLoads complete loads array
   * @param plannedLoads planned loads array
   */
  public setLoads(
    activeLoads: Load[],
    completeLoads: Load[],
    plannedLoads: Load[]
  ) {
    setData(activeLoads, this.activeLoads$);
    setData(completeLoads, this.completeLoads$);
    setData(plannedLoads, this.plannedLoads$);
  }
  /**
   * Sets initially jobsites
   * @param jobsites jobsites array
   */
  public setJobsites(jobsites: JobsiteOption[]) {
    setData(jobsites, this.jobsitesToChoose$);
  }

  private _subscriptions = [];

  private _jobsiteSummary$ = new ReplaySubject<number>(1);
  private _shipmentLocationsCollection$ = new Subject<ShipmentLocationDTO[]>();
  private _unit$ = new ReplaySubject<string>(1);

  /**
   * Initiates updates based on new jobsite has chosen
   * @param jobsiteId jobsite id
   */
  public fetchNewQueryByJobsite(jobsiteId: number) {
    this._jobsiteSummary$.next(jobsiteId);
  }

  /**
   * Initiates updates based on new date range has chosen
   * @param date date range
   */
  public fetchNewQueryByDateRange(date: DateRange) {
    this.dateRange$.next(date);
  }

  /**
   * Sets data for locations to choose from
   * @param locations shipment locations collection
   */
  private _setShipmentLocationsCollection(locations: ShipmentLocationDTO[]) {
    this._shipmentLocationsCollection$.next(locations);
    this.jobsitesToChoose$.next(
      locations.map(item => ({
        id: String(item.shipmentLocationId),
        name: item.shipmentLocationDesc
      }))
    );

    if (locations.length === 0) {
      this.setStatus(LoadingStatus.NOJOBSITES);
    }
  }

  /**
   * The event stream, which handles changes of inputs and fetches new data
   */
  private _fetchingStream: Subscription;

  /**
   * It initiates listening for inputs, fetches needed data from API.
   * One should call it if it intents to get data from API.
   *
   * fetches jobsites collection and orders' loads for the first jobsite
   *
   * @param customerId legal entity Id for jobsite API request
   * @param date date range to fetch
   * @returns nothing
   */
  public fetchInit(customerId: number, date: DateRange, country: string) {
    this.setStatus(LoadingStatus.PENDING);

    // Date is already formatted
    // const from = removeTimeZone(
    //   moment(date.from).format('YYYY-MM-DD T00:00:00')
    // );
    // const to = removeTimeZone(moment(date.to).format('YYYY-MM-DD T23:59:59'));

    this.dateRange$.next(date);

    this.subscribe(
      this.fetchParameters(country),
      parameters => {
        if (parameters && parameters.length) {
          const parameter = parameters.find(
            ({ parameterDesc }) =>
              parameterDesc === 'UNIT_OF_MEASUREMENT_FOR_INDICATORS'
          );
          if (parameter) {
            setData(parameter.parameterValue, this._unit$);
            return;
          }
        }
        console.error(
          'Something is wrong with parameter fetching request. ',
          "Parameter 'UNIT_OF_MEASUREMENT_FOR_INDICATORS' couldn't be found, fall back to default value"
        );
        setData(UNIT_OF_MEASUREMENT_FOR_INDICATORS, this._unit$);
      },
      'error while fetching parameters',
      undefined,
      false
    );

    this.subscribe(
      this.fetchShipmentLocations(customerId),
      ({ shipmentLocations: locations }) => {
        this._setShipmentLocationsCollection(locations);
      },
      'error while fetch shipment locations',
      undefined,
      false
    );

    if (this._fetchingStream) {
      this._fetchingStream.unsubscribe();
    }
    this._fetchingStream = this.subscribe(
      this.createStream(customerId),
      () => {},
      '',
      undefined,
      false
    );
  }

  /**
   * Initializes fetching routine, resulting observable will recover after error
   * @returns combined observables
   */
  createStream(customerId: number) {
    return combineLatest([
      this._jobsiteSummary$,
      this.dateRange$,
      this.unit$
    ]).pipe(
      switchMap(([jobsiteId, { from, to }, unit]) => {
        // remove timezone
        from = moment(from)
          .startOf('day')
          .format('YYYY-MM-DDTHH:mm:ss');
        to = moment(to)
          .endOf('day')
          .format('YYYY-MM-DDTHH:mm:ss');

        this.setStatus(LoadingStatus.PENDING);
        // ATTENTION: country code might should be trimmed
        // Number.isNaN(jobsiteId) is jobsiteId is non we will use all Jobsites
        if (Number.isNaN(jobsiteId)) {
          return this.fetchJobsitesSummary(from, to, customerId).pipe(
            switchMap(({ jobsites }) => {
              const jobsiteIds = jobsites.map(({ jobsiteId }) => jobsiteId);

              return defer(() =>
                combineLatest(
                  jobsiteIds.map(id => this.fetchOrders(from, to, id))
                )
              ).pipe(
                map(reqOrders => reqOrders.map(({ Orders }) => Orders)),
                reduce((acc, curr) => acc.concat(...curr), [] as OrderDTO[]),
                map(orders => [{ Orders: orders }, { jobsites }, unit])
              );
            })
          );
        }

        return forkJoin([
          this.fetchOrders(from, to, jobsiteId),
          this.fetchJobsitesSummary(from, to, customerId)
        ]).pipe(map(data => [...data, unit]));
      }),
      withLatestFrom(this._jobsiteSummary$, this.isNewDashboard2025Enabled$),
      map(
        ([
          [{ Orders: orders }, { jobsites }, unit],
          jobsiteId,
          isNewDashboard2025Enabled
        ]) => {
          const allJobsites = Number.isNaN(jobsiteId);
          const jobsite = jobsites.find(item => item.jobsiteId === jobsiteId);
          if (allJobsites || jobsite) {
            this.updateLoads(
              orders,
              allJobsites
                ? // If we need All Jobsite we merge them into one with all Orders
                  jobsites.reduce(
                    (acc, curr) => ({
                      ...acc,
                      orders: [
                        ...acc.orders,
                        ...curr.orders.map(order => ({
                          ...order,
                          jobsite: curr
                        }))
                      ]
                    }),
                    { orders: [] } as JobsiteDTO
                  )
                : {
                    ...jobsite,
                    orders: jobsite.orders.map(order => ({ ...order, jobsite }))
                  },
              unit,
              isNewDashboard2025Enabled
            );
            this.setStatus(LoadingStatus.RESOLVED);
          } else {
            this.setStatus(LoadingStatus.RESOLVED);
            setData([], this.activeLoads$);
            setData([], this.completeLoads$);
            setData([], this.plannedLoads$);
          }
        }
      ),
      catchError(error => {
        this._jobsiteSummary$.unsubscribe();
        this._jobsiteSummary$ = new ReplaySubject<number>(1);
        this.subscribe(this.createStream(customerId), () => {}, '');
        return throwError(error);
      })
    );
  }

  /**
   * Computes and assigns new value for output arrays of loads, both active and complete
   * @param orders orders to filter and pick needed data from
   * @param jobsite jobsite summary to count orders' loads data from
   * @param unit translation label for unit of measurement
   */
  private updateLoads(
    orders: OrderDTO[],
    jobsite: JobsiteDTO,
    unit: string,
    isNewDashboard2025Enabled: boolean
  ) {
    const activeLoads = [];
    const completeLoads = [];
    const plannedLoads = [];

    for (const order of orders) {
      if (
        ![
          OrderStatusCode.Completed,
          OrderStatusCode.CompletedOnline,
          OrderStatusCode.InProgress,
          OrderStatusCode.InProgressOnline,
          OrderStatusCode.OnHold,
          OrderStatusCode.OnHoldOnline,
          OrderStatusCode.NewOnHold,

          ...(isNewDashboard2025Enabled
            ? [
                OrderStatusCode.NotStartedOnline,
                OrderStatusCode.Confirmed,
                OrderStatusCode.ConfirmedOnline,
                OrderStatusCode.ToBeConfirmed,
                OrderStatusCode.ToBeConfirmedOnline,
                OrderStatusCode.Pending,
                OrderStatusCode.PendingCancelation,
                OrderStatusCode.PendingModification,
                OrderStatusCode.InReview,
                OrderStatusCode.InEdit
              ]
            : [])
        ].includes(order.orderStatusGroupCode)
      )
        continue;
      const orderSummary = jobsite.orders.find(
        item => `${item.orderId}` === `${order.orderId}`
      );

      const rmxUnit = unit ? unit : order.orderItem.length > 0 ? order.orderItem[0].unitDesc : UNIT_OF_MEASUREMENT_FOR_INDICATORS;

      if (orderSummary) {
        const loadObject: Load = {
          volume: order.isReadyMix
            ? `${orderSummary.deliveredQuantity} / ${orderSummary.totalQuantity} ${rmxUnit}`
            : order.orderItem.length > 1
              ? '-'
              : `${order.orderItem[0].productQuantity} ${order.orderItem[0].unitDesc}`,
          load: `${orderSummary.totalDeliveries} / ${orderSummary.totalLoads}`,
          orderId: `${order.orderId}`,
          orderCode: order.orderCode,
          purchaseOrder: order.purchaseOrder,
          instructionsDesc:
            order.instructionsDesc === '' ? '-' : order.instructionsDesc,
          status: order.orderStatusGroupCode,
          deliveryDateTime: orderSummary.requestedOrderDateTime,
          jobsiteDesc: orderSummary.jobsite.jobsiteDesc,
          order: {
            ...order,
            ...orderSummary
          }
        };
        loadObject.products = this.calculateProducts(order);

        if (
          [
            OrderStatusCode.InProgress,
            OrderStatusCode.InProgressOnline,
            OrderStatusCode.OnHold,
            OrderStatusCode.OnHoldOnline,
            OrderStatusCode.NewOnHold
          ].includes(order.orderStatusGroupCode)
        ) {
          activeLoads.push(loadObject);
        } else if (
          [OrderStatusCode.Completed, OrderStatusCode.CompletedOnline].includes(
            order.orderStatusGroupCode
          )
        ) {
          completeLoads.push(loadObject);
        } else if (
          [
            OrderStatusCode.Confirmed,
            OrderStatusCode.ConfirmedOnline,
            OrderStatusCode.ToBeConfirmed,
            OrderStatusCode.ToBeConfirmedOnline,
            OrderStatusCode.Pending,
            OrderStatusCode.PendingCancelation,
            OrderStatusCode.PendingModification,
            OrderStatusCode.InReview,
            OrderStatusCode.InEdit,
            OrderStatusCode.NotStartedOnline
          ].includes(order.orderStatusGroupCode)
        ) {
          plannedLoads.push(loadObject);
        }
      }
    }

    // sort active orders to start with In Progress orders
    activeLoads.sort((a, b) => {
      const statusOrder = {
        [OrderStatusCode.InProgress]: SortingOrder.top,
        [OrderStatusCode.InProgressOnline]: SortingOrder.middle
      };
      const statusA = statusOrder[a.status] || SortingOrder.bottom;
      const statusB = statusOrder[b.status] || SortingOrder.bottom;
      return statusA - statusB;
    });

    setData(activeLoads, this.activeLoads$);
    setData(completeLoads, this.completeLoads$);
    setData(plannedLoads, this.plannedLoads$);
  }

  private calculateProducts(order: OrderDTO) {
    const products: OrderItemWithValue[] = [];
    try {
      for (const item of order.orderItem) {
        const product = products.find(
          p => p.productCode === item.productCode && p.unitId === item.unitId
        );

        // check if the product is already counted
        if (product !== undefined) {
          const pos = products
            .map(e => e.productCode)
            .indexOf(product.productCode);
          products[pos].value += item.productQuantity;
          continue;
        }
        products.push({
          ...item,
          value: item.productQuantity
        });
      }
    } catch (error) {
      console.error('Something went wrong at products calculation', error);
    }
    return products;
  }

  /**
   * Fetches jobsite summary
   * @param from from date
   * @param to to date
   * @param customerId customer id
   * @returns http request observable
   */
  private fetchJobsitesSummary(from: string, to: string, customerId: number) {
    return this.httpClient.get<{ jobsites: JobsiteDTO[] }>(
      `${PathConstants.JOBSITES}?dateFrom=${from}&dateTo=${to}&customerId=${customerId}&isWeb=true`
    );
  }

  /**
   * fetches shipment locations
   * @param customerId customer id
   * @returns http request observable
   */
  private fetchShipmentLocations(customerId: number) {
    return this.httpClient.get<{ shipmentLocations: ShipmentLocationDTO[] }>(
      PathConstants.SHIPMENT_LOCATIONS`${customerId}`
    );
  }

  /**
   * Fetches orders for jobsite
   * @param from from date
   * @param to to date
   * @param jobsiteId jobsite id
   * @returns http request observable
   */
  private fetchOrders(from: string, to: string, jobsiteId: number) {
    return this.httpClient.get<{ Orders: OrderDTO[] }>(
      PathConstants.ORDERS +
        jobsiteId +
        '/orders?startdate=' +
        from +
        '&enddate=' +
        to
    );
  }

  /**
   * Fetches parameter for load quantity
   * @param country country
   * @returns http request observable
   */
  private fetchParameters(country: string) {
    // YSK... the country parameter - from user_type:normal is from 'userInfo.country' and from user_type:guest is from 'order.countryCode'
    return this.httpClient
      .get<
        [{ parameterValue: string; parameterDesc: string }]
      >(PathConstants.MY_PARAMETERS`${country}`)
      .pipe(
        catchError(() => {
          return of([]);
        })
      );
  }

  /**
   * Subscribes on a subject, register it to unsubscribe later, sets error handler
   * @param subject$ subject to subscribe for
   * @param callback next function
   * @param message error message
   * @param onError error callback
   * @returns subscription
   */
  private subscribe<T>(
    subject$: Observable<T>,
    callback: (ang0: T) => void,
    message: string,
    onError?: () => void,
    unsubscribeLater: boolean = true
  ) {
    const s = subject$.subscribe(callback, error => {
      console.error(message ? message : '', error);
      this.setStatus(LoadingStatus.ERROR);
      if (onError) {
        onError();
      }
    });
    if (unsubscribeLater) {
      this._subscriptions.push(s);
    }
    return s;
  }

  ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    if (this._fetchingStream) {
      this._fetchingStream.unsubscribe();
    }
  }
}

function removeTimeZone(date: string) {
  const value = moment(date, 'YYYY-MM-DD THH:mm:ssZ')
    .locale('en')
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss');
  return value;
}

export enum LoadingStatus {
  IDLE = 'IDLE',
  PENDING = 'PENDING',
  RESOLVED = 'RESOLVED',
  ERROR = 'ERROR',
  NOJOBSITES = 'NOJOBSITES'
}

/**
 * Convenient helper function to update the subject
 * @param data data to set
 * @param data$ subject to update
 */
function setData(
  data: Load[] | JobsiteOption[] | string | any,
  data$: Subject<Load[] | JobsiteOption[] | string | any>
) {
  data$.next(data);
}

enum SortingOrder {
  top = 1,
  middle = 2,
  bottom = 3
}
